import { Element, Frame, useEditor } from "@craftjs/core";
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import Editor from "../SegmentBuilder/Editor";
import { getSegmentDetails } from "../../services/api";
import { SegmentProvider } from "../ViewWheel/SegmentContext";
import { EmptyContainerWidget } from "../../components/SegmentBuilder/CodeBlocks";
import { usePaletteColor } from "../../hooks/usePaletteColor";
import { patterns } from "../../components/SegmentBuilder/Shared/Wallpaper/Wallpaper.styled";
import * as Styled from "./styles";
import { useDispatch } from "react-redux";
import {
  setSelectedSegmentColor,
  setSelectedSegmentPalette,
  setSelectedWheelPalette,
} from "../../redux/reducers/colorPalette";
import { Box } from "@mui/material";

const convertSnakeToCamel = (str) => {
  return str.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
};

const convertObjectKeys = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map(convertObjectKeys);
  }

  if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = convertSnakeToCamel(key);
      acc[camelKey] = convertObjectKeys(obj[key]);
      return acc;
    }, {});
  }

  return obj;
};

const SegmentContent = ({ segmentData, isLoading }) => {
  const { actions } = useEditor((state) => ({
    enabled: state.options.enabled,
  }));

  const { getPaletteColor } = usePaletteColor();

  const { settings: segmentSettings = {} } = segmentData || {};
  const {
    wallpaperTransparency,
    wallpaperOverlayColor,
    bgWallpaper,
    isPattern,
    color,
    segmentBgColor,
    currentBg,
    patternColor,
    patternId,
    wallpaperHuePercentage,
    wallpaperHueColor,
  } = segmentSettings;

  useEffect(() => {
    if (segmentData?.nodes) {
      try {
        actions.deserialize(JSON.parse(segmentData.nodes));
      } catch (error) {
        console.error("Error deserializing nodes:", error);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segmentData]);

  const isSegmentEmpty = () => {
    if (!segmentData?.nodes) return true;

    try {
      const nodeMap =
        typeof segmentData.nodes === "string"
          ? JSON.parse(segmentData.nodes)
          : segmentData.nodes;

      const rootNode = JSON.parse(nodeMap).ROOT;
      return !rootNode?.nodes || rootNode.nodes.length === 0;
    } catch (error) {
      return true;
    }
  };

  return (
    <div>
      <SegmentProvider selectedArc={segmentData}>
        <Styled.Container
          color={getPaletteColor(color)}
          currentBg={currentBg}
          segmentBgColor={
            currentBg === 2
              ? `${segmentBgColor} 1`
              : getPaletteColor(segmentBgColor)
          }
          bgWallpaper={bgWallpaper}
          isPattern={isPattern}
          wallpaperOverlayColor={wallpaperOverlayColor}
        >
          {currentBg === 2 && (
            <Styled.BackgroundImageCover
              currentBg={currentBg}
              bgWallpaper={bgWallpaper}
              wallpaperTransparency={wallpaperTransparency}
              wallpaperHuePercentage={wallpaperHuePercentage}
              wallpaperHueColor={wallpaperHueColor}
            />
          )}

          {currentBg === 1 && patternId !== 0 && (
            <Styled.PatternContainer
              wallpaperTransparency={wallpaperTransparency}
              wallpaperOverlayColor={wallpaperOverlayColor}
            >
              <div style={{ marginTop: "-100%" }}>
                {patterns.find((pattern) => pattern.id === patternId)
                  ?.component &&
                  React.createElement(
                    patterns.find((pattern) => pattern.id === patternId)
                      .component,
                    { color: getPaletteColor(patternColor) }
                  )}
              </div>
            </Styled.PatternContainer>
          )}

          {currentBg === 1 && patternId === 0 && (
            <Styled.PatternContainer
              wallpaperTransparency={wallpaperTransparency}
              wallpaperOverlayColor={"white"}
            />
          )}

          {isSegmentEmpty() && !isLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100vh",
                color: "#666",
                fontSize: "16px",
                fontFamily: "DM Sans",
                textAlign: "center",
              }}
            >
              This segment is empty
            </Box>
          ) : (
            <Frame>
              <Element
                canvas
                is={EmptyContainerWidget}
                data-cy="root-container"
              ></Element>
            </Frame>
          )}
        </Styled.Container>
      </SegmentProvider>
    </div>
  );
};

const Segment = () => {
  const { id } = useParams();
  const [preloadedData, setPreloadedData] = React.useState(null);
  const [checkedPreload, setCheckedPreload] = React.useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    // Check for preloaded data injected by React Native WebView
    if (window.preloadedData) {
      setPreloadedData(window.preloadedData);
    }
    setCheckedPreload(true);
  }, [id]);

  const {
    data: segmentData,
    isLoading,
  } = useQuery(["segmentDetails", id], () => getSegmentDetails(id), {
    enabled: checkedPreload && !preloadedData && !!id, // Only fetch if not preloaded
  });

  // Use preloaded data if available
  const dataToUse = preloadedData || convertObjectKeys(segmentData?.data?.data);
  const convertedData = convertObjectKeys(dataToUse);

  useEffect(() => {
    dispatch(setSelectedSegmentPalette(convertedData?.settings?.paletteData));
    dispatch(setSelectedWheelPalette(convertedData?.settings?.wheelPalette));
    dispatch(setSelectedSegmentColor(convertedData?.settings?.color));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convertedData]);

  if (!checkedPreload) {
    return <div />;
  }

  if (!convertedData && (isLoading || !checkedPreload)) {
    return <div />;
  }

  return (
    <Editor enabled={false}>
      <SegmentContent segmentData={convertedData} isLoading={isLoading} />
    </Editor>
  );
};

export default Segment;
